// src/services/enrollmentRequestService.js
//
// Coordinator enrollments → admin approval → the study-patients master list.
//
// When a coordinator records "enrolled in study X" on the patient page, a
// request is written to `enrollment_requests` (site-scoped). An admin reviews
// it on /admin/study-patients → "Pending approval":
//   - Approve: adds the patient to studies_copy/{studyId}/patients with the
//     patient number the admin types (same shape as a manual add), or links a
//     row already on the list instead of adding a second one. The patient's
//     entry becomes approval: "approved".
//   - Reject: the patient's entry becomes approval: "rejected" (kept on the
//     record, ignored by the move rule).
// The master list only ever changes on Approve. Requests are never deleted
// (rules deny it); each decision stays with who/when. Every step is audited on
// the patient's trail.

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import {
  getConsentsPath,
  getDuplicatesPath,
  getEnrollmentRequestsPath,
  getStudyPatientsPath,
} from "./firestorePaths";
import { updateSheetEnrollment } from "./sheetPatientsService";
import { logPatientAuditEvent, getMrnLast4 } from "./patientAuditService";
import { mergeEntryUpdates, entryKey, normalizeEnrolledStudies } from "./enrollmentRules";
import { findMasterListMatches } from "./enrollmentMatch";

export const REQUEST_PENDING = "pending";

// One request per enrollment entry. Deterministic, so saving twice never
// creates two requests for the same pick.
export const requestIdFor = (patientId, entry) =>
  `${patientId}__${entry.studyId}__${Date.parse(entry.enrolledAt) || 0}`.replace(/[/.#[\]]/g, "_");

const me = () => auth.currentUser?.email || "unknown";

/**
 * Create a pending request for each newly added coordinator entry. Skips any
 * that already exist. Fail-soft per request: the patient save has already
 * happened, so a failure here is logged and reported, not thrown.
 * @returns {Promise<number>} how many failed
 */
export async function createEnrollmentRequests({ patientId, source, patientName, dob, mrn, entries }) {
  let failed = 0;
  for (const e of entries || []) {
    try {
      const ref = doc(db, getEnrollmentRequestsPath(), requestIdFor(patientId, e));
      const existing = await getDoc(ref);
      if (existing.exists()) continue;
      await setDoc(ref, {
        patientId,
        source, // "sheet" | "firestore" | "duplicates"
        patientName: patientName || "",
        dob: dob || "",
        mrnLast4: getMrnLast4(mrn),
        studyId: e.studyId,
        studyName: e.studyName || "",
        disease: e.disease || "",
        entryEnrolledAt: e.enrolledAt || null,
        entrySource: e.source || "coordinator",
        requestedBy: e.enrolledBy || me(),
        requestedAt: new Date().toISOString(),
        status: REQUEST_PENDING,
      });
    } catch (err) {
      console.error("Couldn't create enrollment request", err);
      failed += 1;
    }
  }
  return failed;
}

/** A dismissed entry withdraws its pending request (kept, marked cancelled). */
export async function cancelEnrollmentRequests(patientId, entries) {
  for (const e of entries || []) {
    try {
      const ref = doc(db, getEnrollmentRequestsPath(), requestIdFor(patientId, e));
      const snap = await getDoc(ref);
      if (!snap.exists() || snap.data().status !== REQUEST_PENDING) continue;
      await updateDoc(ref, {
        status: "cancelled",
        cancelledAt: new Date().toISOString(),
        cancelledBy: me(),
      });
    } catch (err) {
      console.error("Couldn't cancel enrollment request", err);
    }
  }
}

export async function fetchPendingEnrollmentRequests() {
  const snap = await getDocs(
    query(collection(db, getEnrollmentRequestsPath()), where("status", "==", REQUEST_PENDING))
  );
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(a.requestedAt).localeCompare(String(b.requestedAt)));
}

/** Every row on a study's master list. */
export async function fetchMasterListRows(studyId) {
  const snap = await getDocs(collection(db, getStudyPatientsPath(studyId)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Rows (already loaded) that look like this request's patient, best first. */
export const matchRequestToRows = (request, rows) =>
  findMasterListMatches({ name: request.patientName, dob: request.dob }, rows);

/** Rows already on the study's master list that look like this patient. */
export async function findExistingMasterListRows(request) {
  return matchRequestToRows(request, await fetchMasterListRows(request.studyId));
}

/**
 * Up-front check for the Pending tab: { [requestId]: matches[] }. Each study's
 * list is read once, however many requests point at it. A list that can't be
 * read leaves its requests out, so their cards show as before.
 */
export async function findListMatchesForRequests(requests) {
  const studyIds = [...new Set((requests || []).map((r) => String(r.studyId)))];
  const rowsByStudy = {};
  await Promise.all(
    studyIds.map(async (id) => {
      try {
        rowsByStudy[id] = await fetchMasterListRows(id);
      } catch (err) {
        console.error("Couldn't read the enrolled list for", id, err);
      }
    })
  );
  const out = {};
  for (const r of requests || []) {
    const rows = rowsByStudy[String(r.studyId)];
    if (rows) out[r.id] = matchRequestToRows(r, rows);
  }
  return out;
}

// Write a field update onto the patient's matching enrollment entry, on
// whichever backend holds them. Merged over the stored copy; nothing else on
// the record is touched.
async function updatePatientEntry(request, patch) {
  const key = { studyId: request.studyId, enrolledAt: request.entryEnrolledAt, source: request.entrySource };
  if (request.source === "sheet") {
    const [r] = await updateSheetEnrollment([
      // onlyExisting: update the entry that's there, never append a partial
      // one (same as the Firestore branch, which refuses if it's gone).
      { token: request.patientId, enrolledStudies: [{ ...key, ...patch }], onlyExisting: true },
    ]);
    if (!r || !r.ok) throw new Error((r && r.error) || "Sheet update failed.");
    return;
  }
  for (const path of [getConsentsPath(), getDuplicatesPath()]) {
    const ref = doc(db, path, request.patientId);
    const snap = await getDoc(ref);
    if (!snap.exists()) continue;
    const fresh = normalizeEnrolledStudies(snap.data().enrolledStudies);
    const k = entryKey(key);
    const current = fresh.find((e) => entryKey(e) === k);
    if (!current) throw new Error("This enrollment is no longer on the patient's record.");
    await updateDoc(ref, {
      enrolledStudies: mergeEntryUpdates(fresh, [{ ...current, ...patch }]),
      lastEditedBy: me(),
      lastEditedAt: serverTimestamp(),
    });
    return;
  }
  throw new Error("Patient record not found.");
}

const audit = (request, fields) =>
  logPatientAuditEvent({
    patientId: request.patientId,
    patientName: request.patientName,
    mrnLast4: request.mrnLast4 || "",
    source: request.source,
    actorEmail: me(),
    actorUid: auth.currentUser?.uid || null,
    ...fields,
    metadata: {
      page: "StudyPatientsPage",
      requestId: request.id,
      studyId: request.studyId,
      studyName: request.studyName,
      ...(fields.metadata || {}),
    },
  });

/**
 * Approve a request.
 * @param {object} request
 * @param {{patientNumber?: string, name: string, dob: string, linkRowId?: string}} opts
 *   linkRowId: the patient is already on the list; link that row, add nothing.
 * @returns {Promise<{ok: boolean, patientSynced: boolean, error?: string}>}
 */
export async function approveEnrollmentRequest(request, { patientNumber, name, dob, linkRowId }) {
  const now = new Date().toISOString();
  const by = me();
  const reqRef = doc(db, getEnrollmentRequestsPath(), request.id);

  // Re-check it's still pending, so two admins can't both add the patient.
  const fresh = await getDoc(reqRef);
  if (!fresh.exists() || fresh.data().status !== REQUEST_PENDING) {
    return { ok: false, patientSynced: false, error: "This request was already handled." };
  }

  let masterRowId = linkRowId || null;
  if (!linkRowId) {
    // The row's id IS the request id, so if approval fails after this write
    // and the admin approves again, it rewrites the same row instead of
    // adding a second one for the same patient.
    await setDoc(doc(db, getStudyPatientsPath(request.studyId), request.id), {
      NAME: name,
      DOB: dob,
      PATIENT_NUMBER: patientNumber,
      CREATED_AT: new Date(),
      APPROVED_FROM_REQUEST: request.id,
      APPROVED_BY: by,
    });
    masterRowId = request.id;
  }

  await updateDoc(reqRef, {
    status: "approved",
    approvedAt: now,
    approvedBy: by,
    masterListRowId: masterRowId,
    linkedExistingRow: Boolean(linkRowId),
    patientNumber: linkRowId ? null : patientNumber,
  });

  let patientSynced = true;
  let syncError = null;
  try {
    await updatePatientEntry(request, { approval: "approved", approvedAt: now, approvedBy: by });
  } catch (err) {
    console.error("Approved, but couldn't update the patient record", err);
    patientSynced = false;
    syncError = err.message || "Couldn't update the patient record.";
  }
  await updateDoc(reqRef, { patientSynced, patientSyncError: syncError });

  await audit(request, {
    title: "Study Enrollment Approved",
    action: "ENROLLMENT_APPROVED",
    changes: [{ field: "enrolledStudies", from: `${request.studyName} (pending)`, to: `${request.studyName} (approved)` }],
    metadata: {
      masterListRowId: masterRowId,
      linkedExistingRow: Boolean(linkRowId),
      patientSynced,
    },
  });

  return { ok: true, patientSynced, error: syncError || undefined };
}

/** Retry writing the decision onto the patient after one that couldn't. */
export async function retryPatientSync(request) {
  const reqRef = doc(db, getEnrollmentRequestsPath(), request.id);
  const patch =
    request.status === "rejected"
      ? {
          approval: "rejected",
          rejectedAt: request.rejectedAt,
          rejectedBy: request.rejectedBy,
          rejectedReason: request.rejectedReason || "",
        }
      : { approval: "approved", approvedAt: request.approvedAt, approvedBy: request.approvedBy };
  try {
    await updatePatientEntry(request, patch);
    await updateDoc(reqRef, { patientSynced: true, patientSyncError: null });
    return { ok: true };
  } catch (err) {
    await updateDoc(reqRef, { patientSyncError: err.message || "Failed." });
    return { ok: false, error: err.message };
  }
}

export async function fetchUnsyncedApprovals() {
  const snap = await getDocs(
    query(collection(db, getEnrollmentRequestsPath()), where("patientSynced", "==", false))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Reject a request. The master list is not touched. */
export async function rejectEnrollmentRequest(request, reason) {
  const now = new Date().toISOString();
  const by = me();
  const reqRef = doc(db, getEnrollmentRequestsPath(), request.id);
  const fresh = await getDoc(reqRef);
  if (!fresh.exists() || fresh.data().status !== REQUEST_PENDING) {
    return { ok: false, error: "This request was already handled." };
  }
  await updateDoc(reqRef, { status: "rejected", rejectedAt: now, rejectedBy: by, rejectedReason: reason || "" });

  let patientSynced = true;
  let syncError = null;
  try {
    await updatePatientEntry(request, {
      approval: "rejected",
      rejectedAt: now,
      rejectedBy: by,
      rejectedReason: reason || "",
    });
  } catch (err) {
    console.error("Rejected, but couldn't update the patient record", err);
    patientSynced = false;
    syncError = err.message || "Couldn't update the patient record.";
  }
  await updateDoc(reqRef, { patientSynced, patientSyncError: syncError });
  await audit(request, {
    title: "Study Enrollment Rejected",
    action: "ENROLLMENT_REJECTED",
    severity: "warning",
    changes: [{ field: "enrolledStudies", from: `${request.studyName} (pending)`, to: `${request.studyName} (rejected)` }],
    metadata: { reason: reason || "", patientSynced },
  });
  return { ok: true, patientSynced };
}
